package com.tovtalk.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.ui.text.LinkAnnotation
import androidx.compose.ui.text.TextLinkStyles
import androidx.compose.ui.text.withLink
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Email
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.tovtalk.di.koinInject
import com.tovtalk.domain.entity.LoginMethod
import com.tovtalk.domain.entity.User
import com.tovtalk.domain.repository.AuthManager
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.presentation.state.UiState
import com.tovtalk.presentation.viewmodel.auth.AuthSheetController
import com.tovtalk.presentation.viewmodel.auth.AuthViewModel
import com.tovtalk.ui.platform.PlatformBackHandler
import com.tovtalk.ui.platform.TovTalkGoogleSignInButton
import com.tovtalk.utils.config.isWeb
import com.tovtalk.utils.strings.StringProvider
import com.tovtalk.utils.strings.TranslationsStore
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch

/**
 * TikTok-style auth bottom sheet shown when a guest tries a restricted action.
 * Observes [AuthViewModel.loginState] / [registerState] and calls
 * [AuthSheetController.onAuthSuccess] on success.
 *
 * Providers: Google + Email. (Apple sign-in is intentionally not supported.)
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AuthBottomSheet(
    controller: AuthSheetController,
    authViewModel: AuthViewModel = koinInject(),
    authManager: AuthManager = koinInject(),
    onShowError: (String) -> Unit = {},
) {
    val state by controller.state.collectAsState()
    if (!state.visible) return

    val sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)
    val sheetScope = rememberCoroutineScope()
    val containerColor = Color(0xFF0A0A0A)

    val subtitle = state.reason
        ?.let { key -> TranslationsStore.get(key) }
        ?: StringProvider.get("auth_sheet_subtitle")

    val loginState by authViewModel.loginState.collectAsState()
    val registerState by authViewModel.registerState.collectAsState()

    var showEmailPanel by remember { mutableStateOf(false) }
    var isRegisterMode by remember { mutableStateOf(false) }
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var fullname by remember { mutableStateOf("") }
    var localError by remember { mutableStateOf<String?>(null) }
    var termsAccepted by remember { mutableStateOf(false) }

    // Observe auth results and close the sheet on success.
    LaunchedEffect(Unit) {
        authViewModel.loginState.collect { s ->
            if (s is UiState.Success<*>) {
                controller.onAuthSuccess()
                authViewModel.resetLoginState()
            }
        }
    }
    LaunchedEffect(Unit) {
        authViewModel.registerState.collect { s ->
            if (s is UiState.Success<*>) {
                controller.onAuthSuccess()
                authViewModel.resetRegisterState()
            }
        }
    }

    val loginError = (loginState as? UiState.Error)?.message
    val registerError = (registerState as? UiState.Error)?.message
    val displayError = localError ?: loginError ?: registerError
    val loading = loginState is UiState.Loading || registerState is UiState.Loading

    ModalBottomSheet(
        onDismissRequest = { controller.dismiss() },
        sheetState = sheetState,
        containerColor = containerColor,
        contentColor = Color.White,
    ) {
        // When the email panel is showing, intercept the platform back gesture
        // so it returns to the provider list instead of dismissing the sheet.
        PlatformBackHandler(
            enabled = showEmailPanel,
            onBack = {
                showEmailPanel = false
                localError = null
            },
        )
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .fillMaxHeight(0.9f)
                .background(containerColor)
                .padding(horizontal = 24.dp)
                .padding(bottom = 32.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Text(
                text = StringProvider.get("auth_sheet_title"),
                color = Color.White,
                fontSize = 22.sp,
                fontWeight = FontWeight.ExtraBold,
                textAlign = TextAlign.Center,
                modifier = Modifier.fillMaxWidth(),
            )
            Spacer(Modifier.height(8.dp))
            Text(
                text = subtitle,
                color = Color.White.copy(alpha = 0.6f),
                fontSize = 13.sp,
                textAlign = TextAlign.Center,
                modifier = Modifier.fillMaxWidth(),
            )
            Spacer(Modifier.height(24.dp))

            if (!showEmailPanel) {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    // Google: use the platform button (Android/iOS/Web already handled).
                    // Hide the sheet visually the moment the button is tapped so the
                    // Google UI has room — the composable stays alive (keeping the
                    // Android ActivityResult launcher / Web coroutine scope / iOS bridge
                    // callbacks intact) so the result still flows back here.
                    TovTalkGoogleSignInButton(
                        onSignInStart = { sheetScope.launch { sheetState.hide() } },
                        onSignInSuccess = { idToken, accessToken ->
                            localError = null
                            if (isWeb()) {
                                authViewModel.loginWithFirebaseToken(idToken)
                            } else {
                                authViewModel.login(idToken, null, LoginMethod.GOOGLE, accessToken)
                            }
                        },
                        onSignInError = { e ->
                            onShowError(e.message ?: "Google sign-in error")
                            controller.dismiss()
                        },
                        modifier = Modifier.fillMaxWidth(),
                        enabled = !loading && termsAccepted,
                    )
                    BrandedEmailButton(
                        enabled = !loading && termsAccepted,
                        onClick = {
                            localError = null
                            showEmailPanel = true
                        },
                    )
                }
            } else {
                EmailPanel(
                    email = email,
                    onEmailChange = { email = it },
                    password = password,
                    onPasswordChange = { password = it },
                    fullname = fullname,
                    onFullnameChange = { fullname = it },
                    isRegisterMode = isRegisterMode,
                    onToggleMode = { isRegisterMode = !isRegisterMode; localError = null },
                    onBack = { showEmailPanel = false; localError = null },
                    loading = loading,
                    termsAccepted = termsAccepted,
                    onSubmit = {
                        localError = null
                        if (isRegisterMode) {
                            if (fullname.isBlank() || email.isBlank() || password.isBlank()) {
                                localError = "Please fill all fields"
                            } else {
                                authViewModel.register(fullname.trim(), email.trim(), password, LoginMethod.EMAIL)
                            }
                        } else {
                            if (email.isBlank() || password.isBlank()) {
                                localError = "Please fill all fields"
                            } else {
                                authViewModel.login(email.trim(), password, LoginMethod.EMAIL)
                            }
                        }
                    },
                )
            }

            if (displayError != null) {
                Spacer(Modifier.height(12.dp))
                Text(
                    text = displayError,
                    color = Color(0xFFFF6B6B),
                    fontSize = 12.sp,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth(),
                )
            }

            Spacer(Modifier.weight(1f))
            TermsCheckboxRow(
                accepted = termsAccepted,
                onAcceptedChange = { termsAccepted = it },
            )
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun EmailPanel(
    email: String,
    onEmailChange: (String) -> Unit,
    password: String,
    onPasswordChange: (String) -> Unit,
    fullname: String,
    onFullnameChange: (String) -> Unit,
    isRegisterMode: Boolean,
    onToggleMode: () -> Unit,
    onBack: () -> Unit,
    loading: Boolean,
    termsAccepted: Boolean,
    onSubmit: () -> Unit,
) {
    val fieldColors = OutlinedTextFieldDefaults.colors(
        focusedTextColor = Color.White,
        unfocusedTextColor = Color.White,
        focusedContainerColor = Color.White.copy(alpha = 0.08f),
        unfocusedContainerColor = Color.White.copy(alpha = 0.08f),
        focusedBorderColor = Color.White.copy(alpha = 0.4f),
        unfocusedBorderColor = Color.White.copy(alpha = 0.2f),
        focusedLabelColor = Color.White.copy(alpha = 0.7f),
        unfocusedLabelColor = Color.White.copy(alpha = 0.5f),
        cursorColor = Color.White,
    )
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        if (isRegisterMode) {
            OutlinedTextField(
                value = fullname,
                onValueChange = onFullnameChange,
                label = { Text(StringProvider.get("auth_full_name")) },
                singleLine = true,
                modifier = Modifier.fillMaxWidth(),
                colors = fieldColors,
            )
        }
        OutlinedTextField(
            value = email,
            onValueChange = onEmailChange,
            label = { Text(StringProvider.get("auth_email_or_username")) },
            singleLine = true,
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
            modifier = Modifier.fillMaxWidth(),
            colors = fieldColors,
        )
        OutlinedTextField(
            value = password,
            onValueChange = onPasswordChange,
            label = { Text(StringProvider.get("auth_password")) },
            singleLine = true,
            visualTransformation = PasswordVisualTransformation(),
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
            modifier = Modifier.fillMaxWidth(),
            colors = fieldColors,
        )
        Button(
            onClick = onSubmit,
            enabled = !loading && termsAccepted,
            modifier = Modifier
                .fillMaxWidth()
                .height(52.dp),
            shape = RoundedCornerShape(10.dp),
            colors = ButtonDefaults.buttonColors(
                containerColor = Color.White,
                contentColor = Color.Black,
            ),
        ) {
            Text(
                text = if (isRegisterMode) StringProvider.get("auth_register_button")
                else StringProvider.get("auth_login_button"),
                fontSize = 15.sp,
                fontWeight = FontWeight.SemiBold,
            )
        }
        androidx.compose.foundation.layout.Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            TextButton(onClick = onBack) {
                Text(
                    text = "\u2190",
                    color = Color.White.copy(alpha = 0.8f),
                    fontSize = 18.sp,
                )
            }
            TextButton(onClick = onToggleMode) {
                Text(
                    text = if (isRegisterMode) StringProvider.get("auth_login_button")
                    else StringProvider.get("auth_no_account"),
                    color = Color.White.copy(alpha = 0.8f),
                    fontSize = 13.sp,
                )
            }
        }
    }
}

@Composable
private fun ProviderButton(
    text: String,
    background: Color,
    contentColor: Color,
    onClick: () -> Unit,
) {
    Button(
        onClick = onClick,
        modifier = Modifier
            .fillMaxWidth()
            .height(52.dp),
        shape = RoundedCornerShape(10.dp),
        colors = ButtonDefaults.buttonColors(
            containerColor = background,
            contentColor = contentColor,
        ),
    ) {
        Text(
            text = text,
            color = contentColor,
            fontSize = 15.sp,
            fontWeight = FontWeight.SemiBold,
        )
    }
}

@Composable
private fun BrandedEmailButton(
    enabled: Boolean,
    onClick: () -> Unit,
) {
    Button(
        onClick = onClick,
        enabled = enabled,
        modifier = Modifier
            .fillMaxWidth()
            .height(56.dp),
        shape = RoundedCornerShape(12.dp),
        colors = ButtonDefaults.buttonColors(
            containerColor = Color.White.copy(alpha = 0.1f),
            contentColor = Color.White,
            disabledContainerColor = Color.White.copy(alpha = 0.05f),
            disabledContentColor = Color.White.copy(alpha = 0.4f),
        ),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.Center,
        ) {
            Icon(
                imageVector = Icons.Outlined.Email,
                contentDescription = null,
                modifier = Modifier.size(24.dp),
            )
            Spacer(Modifier.width(12.dp))
            Text(
                text = StringProvider.get("auth_continue_email"),
                fontSize = 15.sp,
                fontWeight = FontWeight.SemiBold,
            )
        }
    }
}

@Composable
private fun TermsCheckboxRow(
    accepted: Boolean,
    onAcceptedChange: (Boolean) -> Unit,
) {
    val uriHandler = LocalUriHandler.current
    val prefix = StringProvider.get("auth_terms_prefix")
    val termsText = StringProvider.get("auth_terms_link")
    val andText = StringProvider.get("auth_terms_and")
    val privacyText = StringProvider.get("auth_privacy_link")

    val linkColor = Color(0xFF4FC3F7)
    val plainColor = Color.White.copy(alpha = 0.6f)

    val linkStyle = SpanStyle(color = linkColor, textDecoration = TextDecoration.Underline)
    val annotated = buildAnnotatedString {
        withStyle(SpanStyle(color = plainColor)) { append("$prefix ") }
        withLink(
            LinkAnnotation.Url(
                url = "https://tovtalk.co.il/terms",
                styles = TextLinkStyles(style = linkStyle),
                linkInteractionListener = { uriHandler.openUri("https://tovtalk.co.il/terms") },
            )
        ) {
            append(termsText)
        }
        withStyle(SpanStyle(color = plainColor)) { append(" $andText ") }
        withLink(
            LinkAnnotation.Url(
                url = "https://tovtalk.co.il/privacy",
                styles = TextLinkStyles(style = linkStyle),
                linkInteractionListener = { uriHandler.openUri("https://tovtalk.co.il/privacy") },
            )
        ) {
            append(privacyText)
        }
    }

    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Checkbox(
            checked = accepted,
            onCheckedChange = onAcceptedChange,
            colors = CheckboxDefaults.colors(
                checkedColor = Color.White,
                uncheckedColor = Color.White.copy(alpha = 0.6f),
                checkmarkColor = Color.Black,
            ),
        )
        Spacer(Modifier.width(4.dp))
        Text(
            text = annotated,
            style = TextStyle(
                fontSize = 12.sp,
                color = plainColor,
            ),
        )
    }
}
